import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, TextField } from '@mui/material';

import { CountryFlag } from '@frontend/features/common/components/CountryFlag';

import {
  adoptDefaultCountry,
  hydratePhoneEditor,
  phoneEditorStatus,
  selectCountry,
  typeNational,
  type PhoneEditorStep,
} from '../phoneEditor';
import {
  PHONE_COUNTRIES,
  defaultPhoneCountry,
  formatInternational,
  phoneCodeOf,
} from '../phoneNumber';

interface Props {
  required: boolean;
  error?: boolean;
  /** Siempre E.164 canónico, o vacío mientras el número no esté completo. */
  value?: string;
  onChange: (value: string) => void;
  label: string;
  /**
   * País con el que arranca el selector: el de la AGENCIA. Gana sobre el
   * idioma del navegador, que solo describe cómo tiene configurado Chrome el
   * asesor — un Chrome en inglés ofrecía `+1` a una agencia uruguaya (#2374).
   */
  defaultCountry?: string;
  /** Permite que un formulario opcional distinga vacío de incompleto. */
  onInvalidChange?: (invalid: boolean) => void;
  disabled?: boolean;
}

/**
 * Teléfono con código de país. Lo que sale es SIEMPRE E.164 canónico, o vacío
 * mientras falte algo: así un número a medio cargar no cuenta como campo
 * satisfecho y el hold sigue bloqueado (el proveedor lo rechazaría igual, pero
 * recién después de crear la reserva).
 *
 * El estado de edición (país elegido, texto tipeado) es del control, no del
 * valor: si se derivara del valor, cada dígito desaparecería mientras el
 * número está incompleto. Toda la lógica vive en `phoneEditor.ts`; esto es un
 * `useState` y dos handlers.
 */
export function PhoneField({
  required,
  error,
  value,
  onChange,
  label,
  defaultCountry,
  onInvalidChange,
  disabled,
}: Props) {
  const { t } = useTranslation();
  // El país de la agencia primero; el idioma del navegador queda de respaldo
  // para cuando la agencia no lo tenga resuelto (una agencia vieja con el país
  // escrito a mano), que es exactamente lo que se hacía antes: así el cambio
  // nunca deja el selector peor de lo que estaba.
  const fallback =
    defaultCountry ||
    defaultPhoneCountry(
      typeof navigator === 'undefined' ? undefined : navigator.language,
    );
  const [state, setState] = useState(
    () => hydratePhoneEditor(value, fallback).state,
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apply = useCallback((step: PhoneEditorStep) => {
    setState(step.state);
    if (step.emit !== undefined) onChangeRef.current(step.emit);
  }, []);

  // Re-sincroniza solo cuando el PADRE cambia el valor (pax sugerido de la
  // venta, pasajeros iniciales, un no canónico que hay que canonizar) y ese
  // cambio no es el eco de lo que este control emitió. Lo que el asesor está
  // tipeando no se toca por un re-render, ni aunque el padre demore en
  // reflejar lo emitido.
  const external = value ?? '';
  const seenExternalRef = useRef<string | null>(null);
  const { emitted, countryCode } = state;
  useEffect(() => {
    if (external === seenExternalRef.current) return;
    seenExternalRef.current = external;
    if (external === emitted) return;
    apply(hydratePhoneEditor(external, countryCode || fallback));
  }, [external, emitted, countryCode, fallback, apply]);

  /**
   * El país de la agencia se resuelve con una query, así que en una carga fría
   * llega DESPUÉS de montar el control: para entonces el selector ya arrancó
   * con la adivinanza del idioma del navegador. Que el estado TENGA país no
   * alcanza para decidir —hay que saber si lo eligió alguien—, y eso lo dice
   * este ref, que se marca en los handlers del asesor y no en la
   * re-sincronización con el padre.
   */
  const chosenByUserRef = useRef(false);
  useEffect(() => {
    if (!defaultCountry) return;
    setState(
      (prev) =>
        adoptDefaultCountry(prev, defaultCountry, chosenByUserRef.current).state,
    );
  }, [defaultCountry]);

  const status = phoneEditorStatus(state);
  const invalid = Boolean(state.national.trim()) && status !== 'complete';
  useEffect(() => {
    onInvalidChange?.(invalid);
  }, [invalid, onInvalidChange]);
  const helperText =
    status === 'no_country'
      ? t('travelServices.booking.phonePickCountry')
      : status === 'empty'
        ? `+${phoneCodeOf(state.countryCode) ?? ''}`
        : status === 'incomplete'
          ? t('travelServices.booking.phoneIncomplete')
          : formatInternational(state.emitted) ?? state.emitted;

  return (
    <Box display="flex" gap={1}>
      <TextField
        select
        size="small"
        sx={{ width: 116, flexShrink: 0 }}
        label={t('travelServices.booking.phoneCountry')}
        value={state.countryCode}
        error={error}
        disabled={disabled}
        onChange={(event) => {
          chosenByUserRef.current = true;
          apply(selectCountry(state, event.target.value));
        }}
      >
        {PHONE_COUNTRIES.map((country) => (
          <MenuItem key={country.code} value={country.code}>
            <Box display="flex" alignItems="center" gap={1}>
              <CountryFlag countryCode={country.code} />
              <span>+{phoneCodeOf(country.code)}</span>
            </Box>
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        fullWidth
        required={required}
        error={error}
        disabled={disabled}
        label={label}
        value={state.national}
        placeholder={t('travelServices.booking.phonePlaceholder')}
        onChange={(event) => {
          chosenByUserRef.current = true;
          apply(typeNational(state, event.target.value));
        }}
        inputProps={{ inputMode: 'tel', maxLength: 20 }}
        helperText={helperText}
      />
    </Box>
  );
}
